import {
  Box,
  H3,
  lightTheme,
  P,
  SystemProblemCategory,
  SystemProblemSeverity,
  Tab,
} from '@vgrubs/components';
import {
  severityColor,
  SYSTEM_PROBLEM_CATEGORIES,
  SYSTEM_PROBLEM_SEVERITIES,
} from '../helpers/systemProblemLabels';
import { useDashboardSystemProblems } from '../hooks/useDashboardSystemProblems';
import { Chips, Counters } from './ProblemSummary.styled';

type Props = {
  severity: SystemProblemSeverity | null;
  /** Toggles: picking the picked one clears it. */
  onSeverity: (severity: SystemProblemSeverity) => void;
  category: SystemProblemCategory | null;
  onCategory: (category: SystemProblemCategory | null) => void;
};

/**
 * The top of the System Problems page: how many are open per severity, and a
 * chip per category with its count - both doubling as the list's filters.
 *
 * The counts are always over every OPEN problem (the Dashboard endpoint
 * computes them that way, whatever is picked), so a number never changes
 * because a filter did. Empty categories are left out, except the one picked:
 * it would be odd for the chip you are on to vanish.
 */
export const ProblemSummary = ({
  severity,
  onSeverity,
  category,
  onCategory,
}: Props) => {
  const { open, bySeverity, byCategory } = useDashboardSystemProblems(null);

  const chips = SYSTEM_PROBLEM_CATEGORIES.filter(
    (chip) => (byCategory?.[chip.value] ?? 0) > 0 || chip.value === category
  );

  return (
    <>
      {/* Selected and not selected the way the Transactions screen draws its
          clickable totals: the picked one a full card, the rest outlined.
          Picking it again clears it. */}
      <Counters>
        {SYSTEM_PROBLEM_SEVERITIES.map(({ value, label }) => {
          const count = bySeverity?.[value] ?? 0;
          const picked = severity === value;
          return (
            <Box
              key={value}
              $pointer
              $box={picked}
              $outlineBox={!picked}
              title={
                picked
                  ? 'Show every severity'
                  : `Show only ${label.toLowerCase()} problems`
              }
              onClick={() => onSeverity(value)}
            >
              <P $m="0">{label} open</P>
              <H3
                $m="0"
                $color={count ? severityColor(value) : lightTheme.lightGray}
              >
                {count}
              </H3>
            </Box>
          );
        })}
      </Counters>

      {/* The app's own small tabs, the count in brackets as on Verifications. */}
      <Chips>
        <Tab
          $small
          selected={category === null}
          onClick={() => onCategory(null)}
        >
          All ({open})
        </Tab>
        {chips.map((chip) => (
          <Tab
            key={chip.value}
            $small
            selected={category === chip.value}
            onClick={() => onCategory(chip.value)}
          >
            {chip.label} ({byCategory?.[chip.value] ?? 0})
          </Tab>
        ))}
      </Chips>
    </>
  );
};
